import { IBooking, IBookingQuoteRevision } from '../interfaces/heavyDutyMachinery/IBooking';

/**
 * Who is allowed to accept a reserva diferida's current price.
 *
 * One rule, stated once, because it applies identically to both sides and
 * had gone wrong on both: **you can only accept a price the OTHER party put
 * on the table.**
 *
 * The newest revision is the live offer. v1 is always the guest's own
 * checkout figure, so at that point only the host can accept it; once the
 * host counters with v2 that revision is theirs, so only the guest can
 * accept. Whoever owns the latest revision is waiting, not deciding.
 *
 * Without this, each side could "accept" its own asking price — and the
 * backend would honour it, since BookingController.AcceptQuote deliberately
 * authorises either party (it has no way to know which of them the UI is
 * actually asking). That would skip the counterparty's approval entirely,
 * which is the single thing the whole deferred-booking flow exists to
 * require.
 *
 * Expressed in terms of who proposed rather than the viewer's role or the
 * version number, so it stays correct however many rounds the negotiation
 * runs.
 */
export const latestQuoteRevision = (booking: IBooking): IBookingQuoteRevision | undefined =>
  booking.quoteHistory?.length
    ? booking.quoteHistory.reduce((latest, revision) => (revision.versionNumber > latest.versionNumber ? revision : latest))
    : undefined;

/**
 * True when the live offer is the viewer's own, i.e. they're waiting on the
 * other party rather than holding a decision. Also true when there's no
 * revision at all, which is the safe reading: never offer an accept button
 * over a price we can't attribute.
 */
export const isAwaitingOtherParty = (booking: IBooking, userId?: string): boolean => {
  const latest = latestQuoteRevision(booking);
  return !latest || latest.proposedByUserId === userId;
};

/**
 * True while there is still a price on the table to argue about.
 *
 * The other half of the rule, and the half that went missing. Accepting,
 * countering and rejecting are all things you do to a LIVE negotiation --
 * the backend says so outright, refusing all three outside AwaitingQuote
 * ("A booking in {status} status has no pending quote to accept/revise/
 * reject"). Once a price is accepted the guest has a figure, a PaymentIntent
 * and a clock; moving the number under them mid-payment is not a thing this
 * flow allows, and the way out for either side is to cancel.
 *
 * It lives here rather than at each call site because the call sites are the
 * problem: MessageThreadPanel gated its "pay" button on the status and its
 * other three buttons on nothing, so a host who had just accepted kept being
 * offered "accept" (the live revision was still the guest's, so the
 * who-proposed-it rule below happily said yes) plus "counter" and "reject",
 * all three of which the server then refused. IncomingBookings and
 * MyBookings only ever got it right because they happen to build their
 * buttons inside an `if (status === 'AwaitingQuote')` branch -- luck of
 * layout, not a rule anyone stated.
 */
export const isQuoteUnderNegotiation = (booking: IBooking): boolean => booking.status === 'AwaitingQuote';

/**
 * Whether the viewer may accept the price as it currently stands: there has
 * to BE a live negotiation, and the offer has to be the other party's.
 */
export const canAcceptQuote = (booking: IBooking, userId?: string): boolean =>
  isQuoteUnderNegotiation(booking) && !isAwaitingOtherParty(booking, userId);

/**
 * Whether the host may counter or reject. No who-proposed-it test: a host can
 * always revise their own counter-offer while the guest is still thinking --
 * what they cannot do is revise a price that has already been accepted.
 * Callers are still responsible for only offering this to the host.
 */
export const canReviseQuote = (booking: IBooking): boolean => isQuoteUnderNegotiation(booking);
